import React from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "react-bootstrap";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { getBackend } from "@/lib/backend/backend2";
import { downloadAndOpenFile } from "@/modules/sa/backendRest";
import { ResourcesLayout } from "@/modules/shared/MainContent";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import { classNames } from "@/lib/util";

type SaveChangesModalProps = {
    show: boolean;
    onSave: () => Promise<void>;
    onCancel: () => void;
};

const SaveChangesModal = (props: SaveChangesModalProps) => {
    return (
        <Modal show={props.show}>
            <Modal.Header>
                <Modal.Title>{translate("common.save", "Save")}</Modal.Title>
                <button type="button" className="close" onClick={props.onCancel}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">
                        <FormattedMessage id="common.close" defaultMessage="Close" />
                    </span>
                </button>
            </Modal.Header>
            <Modal.Body>{translate("common.resource_edit_modal", "Do you wish to save your changes?")}</Modal.Body>
            <Modal.Footer>
                <button className="btn btn-primary" onClick={props.onSave}>
                    <FormattedMessage id="common.yes" defaultMessage="Yes" />
                </button>
                <button className="btn btn-outline-secondary" onClick={props.onCancel}>
                    <FormattedMessage id="common.no" defaultMessage="No" />
                </button>
            </Modal.Footer>
        </Modal>
    );
};

type ItemListProps = {
    keys: string[];
    selected_key: string;
    onClick: (key: string) => void;
    onDelete: (key: string) => Promise<void>;
};

const ItemList = (props: ItemListProps) => {
    const [show_delete_confirm_modal, setShowDeleteConfirmModal] = React.useState(false);

    const handleShowDeleteConfirmationModal = (e: React.MouseEvent<HTMLButtonElement>, item: string) => {
        e.stopPropagation();
        props.onClick(item);
        setShowDeleteConfirmModal(true);
    };

    const handleDeleteConfirm = async () => {
        setShowDeleteConfirmModal(false);
        await props.onDelete(props.selected_key);
    };

    const handleHideDeleteConfirmModal = () => {
        setShowDeleteConfirmModal(false);
    };

    const list = props.keys.map((item, i) => {
        return (
            <li className="nav-item" key={i} onClick={() => props.onClick(item)}>
                <div
                    className={classNames("nav-link d-flex justify-content-between", {
                        "active show": item === props.selected_key,
                    })}
                >
                    <span>{item}</span>
                    <button
                        onClick={e => handleShowDeleteConfirmationModal(e, item)}
                        className="btn btn-short btn-outline-secondary"
                        title={translate("common.delete", "Delete")}
                    >
                        <i className="fas fa-trash align-middle" />
                    </button>
                </div>
            </li>
        );
    });

    return (
        <>
            <ul className="nav nav-tabs">
                <div className="nav-tabs-parent">{list}</div>
            </ul>
            <ConfirmationModal
                show={show_delete_confirm_modal}
                title={translate("common.delete", "Delete")}
                accept_label={translate("common.delete", "Delete")}
                cancel_label={translate("common.cancel", "Cancel")}
                dangerous={true}
                onAccept={handleDeleteConfirm}
                onCancel={handleHideDeleteConfirmModal}
            >
                {translate("common.are_you_sure_delete_file", "Are you sure you want to delete this file?")}
                <br />
                {props.selected_key}
            </ConfirmationModal>
        </>
    );
};

/**
 * BOSCH POC hack: REMOVE THIS LATER
 * Mapping the shift string to a number (FS -> 1, SP -> 2, NS -> 3, 0 -> other)
 * This is bosch specific, and it's used to convert the .json file to .csv
 */
const getShiftNumber = (shift: string): number => {
    if (shift === "FS" || shift === "GZ") {
        return 1;
    } else if (shift === "SP") {
        return 2;
    } else if (shift === "NS") {
        return 3;
    }
    return 0;
};

type Attendance = {
    employee: { persoNr: string; name: string; costCenter: string };
    dayPrograms: { date: string; status: string }[];
};

type AttendanceFileJson = {
    attendances?: Attendance[];
};

/**
 * BOSCH POC hack: REMOVE THIS LATER
 */
const processPocBoschExceptions = async (file_name: string, file: File): Promise<boolean> => {
    // file for 02_Availability.json (anwesenheit) is special, we need to parse it and convert it to csv
    if (file_name.toLowerCase().startsWith("02_ava") && file_name.toLowerCase().endsWith(".json")) {
        let new_file_content = "date;shift;shift_number;person_id;person_name;cost_center\n";

        let file_json_parsed: AttendanceFileJson = {};
        let file_json = "";
        try {
            // incoming file is .json, we need to convert it to .csv
            file_json = await file.text();
            file_json_parsed = JSON.parse(file_json);
        } catch (error) {
            console.error("Error parsing the file: ", error);
            return true;
        }

        const attendances = file_json_parsed.attendances;

        if (attendances === undefined) {
            console.log("attendances is undefined");
            return true;
        }

        console.log("attendances: ", attendances.length);

        // for each employee, we need to get the person_id, person_name, cost_center
        for (const key of attendances) {
            const employee = key.employee;

            const employee_id = employee.persoNr;
            const employee_name = employee.name;
            const cost_center = employee.costCenter;

            const day_programs = key.dayPrograms;

            // for each day_program, we need to get the date, shift, shift_number, person_id, person_name, cost_center
            for (const day_program of day_programs) {
                const date = String(day_program.date).substring(0, 10);
                const shift = day_program.status;
                const shift_number = getShiftNumber(shift);

                if (shift_number === 0) {
                    continue;
                }

                const current_date = new Date();

                const one_month_from_now_past = new Date();
                one_month_from_now_past.setMonth(current_date.getMonth() - 1);

                const five_months_from_now = new Date();
                five_months_from_now.setMonth(current_date.getMonth() + 5);

                const date_object = new Date(date);

                // Check if the date_object is within the range of one month past and five months from now.
                // If it's not within this range, skip the current iteration and continue with the next one.
                if (
                    !(
                        date_object.getTime() >= one_month_from_now_past.getTime() &&
                        date_object.getTime() <= five_months_from_now.getTime()
                    )
                ) {
                    continue;
                }

                // i added employee_name to be ready to be used in the future (if needed for inserting new people)
                // shift is also added just for checking
                new_file_content += `${date};${shift};${shift_number};${employee_id};${employee_name};${cost_center}\n`;
            }
        }

        // converted file from .json to .csv
        await getBackend().common.setBlobStorageItemAsString({
            content: new_file_content,
            key: "02_Availability.csv",
        });

        // this to not throw an error on the gui
        await getBackend().common.setBlobStorageItemAsString({
            content: "",
            key: file_name,
        });

        return true;
    } else if (
        file_name.toLowerCase().startsWith("current_qualification_production") &&
        file_name.toLowerCase().endsWith(".csv")
    ) {
        await getBackend().common.setBlobStorageItemAsString({
            content: await file.text(),
            key: file_name,
        });
        await getBackend().manufacturing.recomputeCompetences({ file_name });
        return true;
    }

    return false;
};

/** Utility function for getting the list of blob-storage items */
async function getItems(): Promise<string[]> {
    const x = await getBackend().common.getBlobStorageItemList({});
    return x.items.map(x => x.key);
}

type Props = {};

type State = {
    content: string;
    error: string;
    items: string[];
    selected_key: string;
    show_changes_modal: boolean;
    original_content: string;
    new_selected_key: string | null;
    show_download_button: boolean;
};

class BlobStorageItems extends React.Component<Props, State> {
    inputOpenFileRef: { current: null | HTMLInputElement };
    // batchopGroupsListRef: { current: null | BatchopGroupsList };
    constructor(props: Props) {
        super(props);

        const state: State = {
            content: "",
            original_content: "",
            error: "",
            items: [],
            selected_key: "",
            new_selected_key: "",
            show_changes_modal: false,
            show_download_button: false,
        };
        this.state = state;
        this.inputOpenFileRef = React.createRef();
    }

    async showKey(key: string): Promise<void> {
        try {
            const additional_info = await getBackend().common.getBlobStorageAdditionalInfo({ key });
            const size = additional_info.size;

            this.setState({ error: "", selected_key: key });
            if ((size && size / (1024 * 1024) < 1) || !size) {
                const res = await getBackend().common.getBlobStorageItemAsString({ key });
                this.setState({ content: res.content, original_content: res.content, show_download_button: false });
            } else {
                this.setState({ content: "", original_content: "", show_download_button: true });
            }
        } catch (err) {
            this.setState({ error: "" + err });
            console.log(err);
        }
    }

    async componentDidMount() {
        try {
            const items = await getItems();
            this.setState({ items });
            if (items.length > 0) {
                await this.showKey(items[0]);
            }
        } catch (err) {
            this.setState({ error: "" + err, items: [], content: "", selected_key: "", original_content: "" });
            console.log(err);
        }
    }

    onCreateNew = async () => {
        this.setState({ error: "" });
        const file_name = (prompt("Enter name for the new file", "") || "").trim();
        if (file_name.length == 0) {
            this.setState({ error: "Name cannot be empty" });
            return;
        }
        if (this.state.items.indexOf(file_name) >= 0) {
            this.setState({ error: "Name already exists" });
            return;
        }
        await getBackend().common.setBlobStorageItemAsString({
            content: "",
            key: file_name,
        });
        const items = await getItems();
        this.setState({ items }, () => {
            this.showKey(file_name);
        });
    };

    onUpload = async () => {
        if (this.inputOpenFileRef.current) {
            this.inputOpenFileRef.current.click();
        }
    };

    setSelectedFile = async (file: File) => {
        const file_name = file.name;
        if (!(await processPocBoschExceptions(file_name, file))) {
            await getBackend().common.setBlobStorageItemAsString({
                content: await file.text(),
                key: file_name,
            });
        }

        alert("File was successfully uploaded");

        const items = await getItems();
        this.setState({ items }, () => {
            this.showKey(file_name);
        });
    };

    onSave = async () => {
        const new_selected_key = this.state.new_selected_key;

        try {
            this.setState({ error: "" });
            await getBackend().common.setBlobStorageItemAsString({
                content: this.state.content,
                key: this.state.selected_key,
            });

            this.setState({
                show_changes_modal: false,
                new_selected_key: null,
                original_content: this.state.content,
            });

            this.showKey(this.state.selected_key);
        } catch (err) {
            this.setState({ error: "" + err, show_changes_modal: false, new_selected_key: null });
            console.log(err);
        }

        if (new_selected_key) {
            this.showKey(new_selected_key);
        }
    };

    onReset = () => {
        this.showKey(this.state.selected_key);
    };

    onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const target = e.target;
        if (target instanceof HTMLTextAreaElement) {
            this.setState({ content: target.value });
        }
    };

    onCancelSaveChangesModal = () => {
        if (this.state.new_selected_key) {
            this.setState(
                {
                    show_changes_modal: false,
                    selected_key: this.state.new_selected_key,
                    new_selected_key: null,
                },
                () => {
                    this.showKey(this.state.selected_key);
                }
            );
        } else {
            this.setState({ show_changes_modal: false });
        }
    };

    onDelete = async (key: string) => {
        try {
            await getBackend().common.deleteBlobStorageItem({ key });
            const items = await getItems();
            this.setState({ items }, () => {
                if (items.length > 0) {
                    this.showKey(items[0]);
                } else {
                    this.setState({ selected_key: "" });
                }
            });
        } catch (error) {
            this.setState({ error: translate("common.error_delete", "An error occurred while deleting data") });
            console.error(error);
        }
    };

    onClickItemList = (new_key: string) => {
        const should_show_changes_modal =
            new_key !== this.state.selected_key &&
            this.state.original_content !== this.state.content &&
            !this.state.show_changes_modal;

        if (should_show_changes_modal) {
            this.setState({ show_changes_modal: true, new_selected_key: new_key });
        } else {
            this.showKey(new_key);
        }
    };

    getTokenAndRedirect = async () => {
        const token = await getBackend().common.getBlobStorageFileToken({ fname: this.state.selected_key });
        downloadAndOpenFile("/direct-file/" + token.token);
    };

    renderContent() {
        if (!this.state.show_download_button) {
            let pretty_text = this.state.content;

            // try to make text pretty if it's json
            if (this.state.content !== "" && this.state.selected_key.toLowerCase().endsWith(".json")) {
                try {
                    pretty_text = JSON.stringify(JSON.parse(this.state.content), null, 4);
                } catch (err) {
                    // do nothing
                }
            }
            return (
                <textarea rows={35} onChange={this.onChange} value={pretty_text} style={{ width: "100%" }}></textarea>
            );
        } else {
            return (
                <div className="mt-4 card-container">
                    <FormattedMessage
                        id="common.unable_to_preview_try_downloading"
                        defaultMessage="Unable to preview file. Try downloading instead"
                    />
                    <button onClick={() => this.getTokenAndRedirect()} className="btn ml-3 mt-4">
                        <FormattedMessage id="common.download" defaultMessage="Download" />
                    </button>
                </div>
            );
        }
    }

    render() {
        return (
            <>
                <SaveChangesModal
                    show={this.state.show_changes_modal}
                    onSave={this.onSave}
                    onCancel={this.onCancelSaveChangesModal}
                />

                <ResourcesLayout>
                    <ResourcesLayout.Header
                        title={
                            <h2>
                                <FormattedMessage id="Header.menu.custom_files" defaultMessage="Custom files" />
                            </h2>
                        }
                        buttons={
                            <>
                                <button
                                    disabled={this.state.original_content === this.state.content}
                                    onClick={() => this.setState({ show_changes_modal: true })}
                                    className="btn ml-3"
                                >
                                    {translate("common.save", "Save")}
                                </button>
                                <button
                                    disabled={this.state.original_content === this.state.content}
                                    onClick={this.onReset}
                                    className="btn ml-3"
                                >
                                    {translate("common.reset", "Reset")}
                                </button>
                                <button onClick={() => this.onCreateNew()} className="btn ml-3">
                                    {translate("common.new", "New")}
                                </button>
                                <input
                                    ref={this.inputOpenFileRef}
                                    type="file"
                                    style={{ display: "none" }}
                                    onChange={e => this.setSelectedFile(e.target.files![0])}
                                />
                                <button onClick={this.onUpload} className="btn ml-3">
                                    {translate("common.upload", "Upload")}
                                </button>
                            </>
                        }
                    />
                    <ResourcesLayout.Main>
                        <ResourcesLayout.List>
                            <ItemList
                                keys={this.state.items}
                                selected_key={this.state.selected_key}
                                onClick={this.onClickItemList}
                                onDelete={this.onDelete}
                            />
                        </ResourcesLayout.List>
                        <ResourcesLayout.Content>
                            <ErrorComponent type="error" msg={this.state.error} />
                            {this.renderContent()}
                        </ResourcesLayout.Content>
                    </ResourcesLayout.Main>
                </ResourcesLayout>
            </>
        );
    }
}
export default BlobStorageItems;
